import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon, IconButton, Logo, Modal } from "@/design-system";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import { initials } from "@/lib/format";
import { GROUP_LABELS, navFor } from "./nav";
import { useTheme } from "./theme";

const KIND_LABELS = {
  dokon: "Doʻkon",
  onlayn: "Onlayn savdo",
  it: "IT va xizmatlar",
  xizmat: "Xizmat koʻrsatish",
  startap: "IT startap",
  umumiy: "Biznes",
};
const GROUP_ORDER = ["asosiy", "moliya", "jamoa", "hujjat"];

function isActive(pathname, to) {
  return to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
}

function NavButton({ item, active, onClick }) {
  return (
    <button
      type="button"
      className={`rs-nav${active ? " active" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      title={item.label}
    >
      <Icon name={item.icon} />
      <span>{item.label}</span>
    </button>
  );
}

/** Bandlarni guruhlab (Asosiy / Moliya / Jamoa / Hujjat) chiqaradi; bitta guruh boʻlsa sarlavhasiz. */
function NavGroups({ items, pathname, go }) {
  const groups = GROUP_ORDER.map((g) => [g, items.filter((n) => n.group === g)]).filter(
    ([, xs]) => xs.length,
  );
  return groups.map(([g, xs]) => (
    <div key={g} className="side-group">
      {groups.length > 1 && <div className="rs-side-h">{GROUP_LABELS[g]}</div>}
      {xs.map((n) => (
        <NavButton key={n.to} item={n} active={isActive(pathname, n.to)} onClick={() => go(n.to)} />
      ))}
    </div>
  ));
}

export function AppShell() {
  const { business, businesses, role, can, switchBusiness, logout, user } = useAuth();
  const { pathname } = useLocation();
  const go = useNavigate();
  const { theme, toggle } = useTheme();
  const [more, setMore] = useState(false);
  const { primary, rest, tabs } = navFor(business?.kind, can);
  useEffect(() => setMore(false), [pathname]);

  return (
    <div className="app">
      <nav className="rs-side side" aria-label="Asosiy menyu">
        <div className="rs-side-logo">
          <Logo onDark />
        </div>
        {businesses.length > 1 ? (
          <label className="rs-biz side-biz">
            <span className="rs-av">{initials(business?.name)}</span>
            <span className="side-biz-txt">
              <b>{business?.name}</b>
              <small>
                {KIND_LABELS[business?.kind] ?? "Biznes"} · {ROLE_LABELS[role] ?? role}
              </small>
            </span>
            <Icon name="chev" size={16} />
            <select
              className="side-biz-select"
              aria-label="Biznesni almashtirish"
              value={business?.id}
              onChange={(e) => switchBusiness(e.target.value)}
            >
              {businesses.map((b) => (
                <option key={b.business.id} value={b.business.id}>
                  {b.business.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="rs-biz side-biz" style={{ cursor: "default" }}>
            <span className="rs-av">{initials(business?.name)}</span>
            <span className="side-biz-txt">
              <b>{business?.name}</b>
              <small>
                {KIND_LABELS[business?.kind] ?? "Biznes"} · {ROLE_LABELS[role] ?? role}
              </small>
            </span>
          </div>
        )}
        <NavGroups items={primary} pathname={pathname} go={go} />
        {rest.length > 0 && (
          <details className="side-more" open={rest.some((n) => isActive(pathname, n.to)) || undefined}>
            <summary className="rs-nav">
              <Icon name="more" />
              <span>Yana</span>
              <span className="rs-ct">{rest.length}</span>
            </summary>
            <NavGroups items={rest} pathname={pathname} go={go} />
          </details>
        )}
        <div className="side-foot">
          <span className="side-user">
            <span className="side-user-av">{initials(user?.full_name ?? user?.email)}</span>
            <span className="side-user-txt">
              <b>{user?.full_name ?? "Foydalanuvchi"}</b>
              <small>{user?.email}</small>
            </span>
          </span>
          <IconButton
            icon={theme === "dark" ? "sun" : "moon"}
            label={theme === "dark" ? "Yorugʻ mavzu" : "Tungi mavzu"}
            onClick={toggle}
          />
          <IconButton icon="logout" label="Chiqish" onClick={logout} />
        </div>
      </nav>

      <main className="main" id="main">
        <Outlet />
      </main>

      <nav className="rs-tabbar tabbar-fixed" aria-label="Asosiy">
        {tabs.map((n) => (
          <button
            key={n.to}
            type="button"
            className={isActive(pathname, n.to) ? "active" : ""}
            aria-current={isActive(pathname, n.to) ? "page" : undefined}
            onClick={() => go(n.to)}
          >
            <Icon name={n.icon} />
            {n.label.split(" ")[0]}
          </button>
        ))}
        <button type="button" className={more ? "active" : ""} onClick={() => setMore(true)}>
          <Icon name="more" />
          Yana
        </button>
      </nav>
      <Modal open={more} title="Boʻlimlar" onClose={() => setMore(false)} size="sm">
        <div className="more-grid">
          {[...primary.slice(4), ...rest].map((n) => (
            <button key={n.to} type="button" onClick={() => go(n.to)}>
              <Icon name={n.icon} size={22} />
              {n.label}
            </button>
          ))}
          <button type="button" onClick={toggle}>
            <Icon name={theme === "dark" ? "sun" : "moon"} size={22} />
            Mavzu
          </button>
          <button type="button" onClick={logout}>
            <Icon name="logout" size={22} />
            Chiqish
          </button>
        </div>
      </Modal>
    </div>
  );
}
